import React, { useState, useEffect, useCallback } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { User, Loader2 } from "lucide-react";
import { Control } from "react-hook-form";
import { peopleAPI } from "@/api/people";
import PersonModal from "@/components/PersonModal";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

type PersonSelectorProps = {
  control: Control<any>;
  disabled?: boolean;
};

const PersonSelector: React.FC<PersonSelectorProps> = ({ control, disabled = false }) => {
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [isAddingPerson, setIsAddingPerson] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchPeople = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await peopleAPI.getPeople();
      // Map the data to simplified format for the dropdown
      const mappedPeople = data.map(person => ({
        id: person.id,
        name: person.name
      }));
      setPeople(mappedPeople);
    } catch (error) {
      
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch people from the API
  useEffect(() => {
    fetchPeople();
    
    // Add listener for people data refresh events
    const handlePeopleDataRefresh = (event: any) => {
      
      fetchPeople();
    };
    
    window.addEventListener('people-data-refresh', handlePeopleDataRefresh);
    
    return () => {
      window.removeEventListener('people-data-refresh', handlePeopleDataRefresh);
    };
  }, [fetchPeople]);

  return (
    <FormField
      control={control}
      name="personId"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-sm text-muted-foreground mb-1">
            İlgili Kişi {disabled && <span className="text-muted-foreground">(Muhtelif seçildi)</span>}
          </FormLabel>
          <Select 
            onValueChange={field.onChange} 
            value={field.value}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger className={`bg-background ${disabled ? 'opacity-50' : ''}`}>
                {isLoading ? (
                  <div className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span>Yükleniyor...</span>
                  </div>
                ) : (
                  <SelectValue placeholder={disabled ? "Muhtelif" : "Kişi seçin (opsiyonel)"} />
                )}
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {people.map(person => (
                <SelectItem key={person.id} value={person.id}>{person.name}</SelectItem>
              ))}
              {people.length === 0 && !isLoading && (
                <div className="py-2 px-4 text-sm text-muted-foreground">
                  Kişi bulunamadı
                </div>
              )}
            </SelectContent>
          </Select>
          {!disabled && (
            <Button 
              type="button"
              variant="outline"
              size="sm"
              className="mt-2 w-full"
              onClick={() => setPersonModalOpen(true)}
              disabled={isAddingPerson}
            >
              {isAddingPerson ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <User className="h-4 w-4 mr-2" />
              )}
              Yeni Kişi Ekle
            </Button>
          )}
          <FormMessage />
          <PersonModal
            isOpen={personModalOpen}
            onClose={() => setPersonModalOpen(false)}
            isEditing={false}
            onSubmit={async (data) => {
              if (!user?.id) {
                toast({
                  title: "Hata",
                  description: "Kullanıcı bilgisi bulunamadı.",
                  variant: "destructive",
                });
                return;
              }

              try {
                setIsAddingPerson(true);
                const newPerson = await peopleAPI.createPerson({
                  name: data.name,
                  category: data.category,
                  specialty: data.specialty,
                  phone: data.phone || "",
                  email: data.email || "",
                  created_by: user.id,
                });

                field.onChange(newPerson.id);

                const event = new CustomEvent("people-data-refresh", {
                  detail: { action: "add", person: newPerson },
                });
                window.dispatchEvent(event);

                toast({
                  title: "Kişi eklendi",
                  description: `${data.name} başarıyla eklendi.`,
                });

                setPersonModalOpen(false);
                await fetchPeople();
              } catch (error: any) {
                toast({
                  title: "Kişi eklenemedi",
                  description: error?.message || "Kişi eklenirken bir hata oluştu.",
                  variant: "destructive",
                });
              } finally {
                setIsAddingPerson(false);
              }
            }}
          />
        </FormItem>
      )}
    />
  );
};

export default PersonSelector;
